import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';
import type { DeviceType } from '@/types/deviceType';

// Define interfaces for topology data

/** VLAN information for a device */
export interface TopologyVlan {
  vlanId: number;
  vlanName: string | null;
  vlanType: string | null;
  status: string;
}

/** VLAN assignment for an interface */
export interface InterfaceVlanInfo {
  vlanId: number;
  isNative: boolean;
  isTagged: boolean;
}

export interface TopologyDevice {
  id: string;
  name: string | null;
  ipAddress: string;
  deviceType: DeviceType;
  status: string;
  regionId: string;
  divisionId: string | null;
  /** Whether the device is actively monitored (has been activated) */
  isMonitored: boolean;
  /** VLANs configured on this device */
  vlans?: TopologyVlan[];
}

export interface InterfaceLagInfo {
  interfaceId: string;
  interfaceName: string;
  relationshipType: string;
  /** VLANs configured on this LAG interface (for member ports pointing to their parent LAG) */
  vlans?: InterfaceVlanInfo[];
}

export interface ConnectionEndpoint {
  deviceId: string;
  deviceName: string | null;
  deviceIp: string;
  interfaceId?: string;
  interfaceName?: string;
  interfaceSpeedMbps?: number;
  /** If this interface is a member of a LAG, info about the parent LAG */
  lagMembership?: InterfaceLagInfo;
  /** If this interface IS a LAG, list of member interfaces */
  lagMembers?: InterfaceLagInfo[];
  /** VLANs assigned to this interface */
  vlans?: InterfaceVlanInfo[];
}

export interface TopologyConnection {
  id: string;
  sourceDeviceId: string;
  targetDeviceId: string;
  connectionType: string;
  confidenceScore: number;
  /** Link speed in bps (optional, from LLDP/SNMP discovery) */
  speedBps?: number;
  /** Source endpoint details */
  source: ConnectionEndpoint;
  /** Target endpoint details */
  target: ConnectionEndpoint;
}

/** Where an end-user device (endpoint) attaches to the topology: either a
 * wired switch port or a wireless AP radio. Fields are split by `kind` —
 * wired attachments carry interface/VLAN info, wireless carries SSID/radio
 * info (and may also carry a VLAN) — and the backend omits whichever half
 * doesn't apply, hence all-optional.
 */
export interface TopologyEndpointAttachment {
  kind: 'wired' | 'wireless';
  /** The attachment's id for the link traffic verdict. */
  linkId?: string;
  deviceId: string;
  /** Wired only */
  interfaceId?: string;
  interfaceName?: string;
  /** Wired or wireless */
  vlanId?: number;
  /** Wireless only */
  ssid?: string;
  band?: string;
  channel?: number;
  signalDbm?: number;
}

/** An end-user device (phone, laptop, etc.) discovered attached to a
 * managed device — rendered as a satellite node in the endpoints overlay.
 * Only present in the topology response when `includeEndpoints=true` was
 * requested AND at least one endpoint exists for the region.
 */
export interface TopologyEndpoint {
  id: string;
  macAddress: string;
  hostname: string | null;
  ouiVendor: string | null;
  classification: string | null;
  attachment: TopologyEndpointAttachment;
}

export interface TopologyData {
  devices: TopologyDevice[];
  connections: TopologyConnection[];
  /** Absent when `includeEndpoints` wasn't requested, or when it was but
   * there are zero endpoints for the region. */
  endpoints?: TopologyEndpoint[];
}

const backendApi = config.backendApi;

interface FetchTopologyParams {
  regionId?: string;
  divisionId?: string;
  /** Ask the backend to include the `endpoints` array in the response. */
  includeEndpoints?: boolean;
}

// The centralized fetch function
const fetchTopology = async (params: FetchTopologyParams): Promise<TopologyData> => {
  const queryParams = new URLSearchParams();

  if (params.regionId) {
    queryParams.append('regionId', params.regionId);
  }

  if (params.divisionId) {
    queryParams.append('divisionId', params.divisionId);
  }

  if (params.includeEndpoints) {
    queryParams.append('includeEndpoints', 'true');
  }

  const queryString = queryParams.toString();
  const url = `${backendApi}/api/topology${queryString ? `?${queryString}` : ''}`;

  const response = await axios.get<TopologyData>(url, { withCredentials: true });
  return response.data;
};

// The custom hook
export function useTopology(params: FetchTopologyParams & { organizationId?: string | null }) {
  const { organizationId, ...fetchParams } = params;
  const contextReady = useActiveContextReady();

  return useQuery<TopologyData>({
    // `fetchParams` (which includes `includeEndpoints`) is part of the key
    // so toggling the endpoints overlay caches under its own key rather than
    // returning a differently-shaped response under the same key — a shape
    // change under a stable key poisons the react-query cache for every
    // other consumer of `['topology', organizationId, ...]`.
    queryKey: ['topology', organizationId, fetchParams],
    queryFn: () => {
      if (!organizationId) {
        throw new Error("organizationId is required to fetch topology.");
      }
      return fetchTopology(fetchParams);
    },
    // Enable the query only if organizationId is available
    enabled: !!organizationId && contextReady,
  });
}
